import React, { useState, useEffect } from "react";
import {
  Table,
  TableHeader,
  TableBody,
  TableVariant,
  wrappable,
} from "@patternfly/react-table";
import { Text, TextVariants } from "@patternfly/react-core";
import isEmpty from "lodash/isEmpty";
import dateFormatConverter from "../../utils/format";
import DeleteConfirmationModal from "../general/DeleteConfirmationModal";
import VersionColorModal from "../general/VersionColorModal";
import versionCategoryConst from "../../constants/versionCategoryConst";
import _orderBy from "lodash/orderBy";

export default (props) => {
  const versions = props.product.versions;
  const isLayeredProduct = props.product.is_layered_product;
  const showLastMinorRelease = props.product.show_last_minor_release;
  // const cellColSpan = !!showLastMinorRelease
  //   ? props.productPhases.length + 3
  //   : props.productPhases.length + 2;
  const cellColSpan =
    (!!showLastMinorRelease ? 1 : 0) +
    (!!isLayeredProduct ? props.product.product_extra_headers.length : 0) +
    props.productPhases.length +
    2;

  const headers = props.productPhases.map((phase) => ({
    title: phase.name,
    transforms: [wrappable],
    props: { style: { maxWidth: 100 } },
  }));

  if (!!isLayeredProduct && props.product.product_extra_headers) {
    let product_extra_headers = _orderBy(props.product.product_extra_headers, ["id"], ["desc"]);
    product_extra_headers.forEach((extra_header) => {
      headers.unshift({
        title: extra_header.header,
        transforms: [wrappable],
        props: { style: { maxWidth: 60 } },
      });
    });
  }

  headers.unshift({
    title: "Weight",
    transforms: [wrappable],
    props: { style: { maxWidth: 50 } },
  });
  headers.unshift({
    title: "Version",
    transforms: [wrappable],
    props: { style: { maxWidth: 100 } },
  });

  if (!!showLastMinorRelease) {
    headers.push({
      title: "Last Minor Release",
      transforms: [wrappable],
      props: { style: { maxWidth: 100 } },
    });
  }

  function convertToVersionRowCells(version) {
    let cells = version.phases
      ? props.productPhases.map((productPhase) => {
        // if comment has value, return comment value, otherwise return enddate
        let matchedPhase = version.phases.find(
          (phase) => phase.name === productPhase.name
        );
        return dateFormatConverter(matchedPhase, false);
      })
      : props.productPhases.map((productPhase) => "N/A");

    // add last minor release if it is enabled
    if (!!showLastMinorRelease) {
      cells.push(
        version.last_minor_release ? version.last_minor_release : "N/A"
      );
    }

    // add base product version if it is layered product
    if (!!isLayeredProduct) {
      let extra_header_values = _orderBy(version.extra_header_values, ["header_id"], ["desc"]);
      extra_header_values.forEach((value) => {
        cells.unshift(
          value.extra_value ? value.extra_value : "N/A"
        );
      });
    }

    cells.unshift(version.weight);
    cells.unshift(version.name);
    return cells;
  }

  function categoryLegendRow(category) {
    return {
      cells: [
        {
          title: (
            <Text
              component={TextVariants.h3}
              className={"versionCategory"}
              style={{ fontWeight: 800 }}
            >
              {category}
            </Text>
          ),
          props: {
            style: versionCategoryConst.versionCategoryStyles,
            title: category,
            colSpan: cellColSpan,
          },
        },
      ],
    };
  }

  function convertVersionsToVersionRows(versions) {
    // This function will seperate versions by category
    // if version.type is null, then put it as 'uncategorized'
    let fullSupportVersions = versions.filter(
      (version) => version.type === "Full Support"
    );
    let maintenanceSupportVersions = versions.filter(
      (version) => version.type === "Maintenance Support"
    );
    let extendedSupportVersions = versions.filter(
      (version) => version.type === "Extended Support"
    );
    let endOfMaintenanceVersions = versions.filter(
      (version) => version.type === "End of Maintenance"
    );
    let EOLVersions = versions.filter(
      (version) => version.type === "End of life"
    );
    let uncategorizedVersions = versions.filter((version) =>
      isEmpty(version.type)
    );
    let versionRows = [];

    if (!isEmpty(fullSupportVersions)) {
      versionRows.push({ version_type: "Full Support" });
      versionRows = versionRows.concat(fullSupportVersions);
    }

    if (!isEmpty(maintenanceSupportVersions)) {
      versionRows.push({ version_type: "Maintenance Support" });
      versionRows = versionRows.concat(maintenanceSupportVersions);
    }

    if (!isEmpty(extendedSupportVersions)) {
      versionRows.push({ version_type: "Extended Support" });
      versionRows = versionRows.concat(extendedSupportVersions);
    }

    if (!isEmpty(endOfMaintenanceVersions)) {
      versionRows.push({ version_type: "End of Maintenance" });
      versionRows = versionRows.concat(endOfMaintenanceVersions);
    }

    if (!isEmpty(EOLVersions)) {
      versionRows.push({ version_type: "End of life" });
      versionRows = versionRows.concat(EOLVersions);
    }

    if (!isEmpty(uncategorizedVersions)) {
      versionRows.push({ version_type: "Uncategorized" });
      versionRows = versionRows.concat(uncategorizedVersions);
    }
    return versionRows;
  }

  function convertToRows(versionRows) {
    return versionRows.map((versionRow) => {
      if (!isEmpty(versionRow.version_type))
        return categoryLegendRow(versionRow.version_type);
      return {
        cells: convertToVersionRowCells(versionRow),
      };
    });
  }

  const [rows, setRows] = useState([]);
  const [versionRows, setVersionRows] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isColorModalOpen, setIsColorModalOpen] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState({});
  const [versionToSetColor, setVersionToSetColor] = useState({});

  function onDeleteModelOpen(event) {
    setIsDeleteModalOpen(true);
  }

  function handleDeleteModalToggle() {
    setIsDeleteModalOpen(!isDeleteModalOpen);
  }

  function onColorModelOpen(event) {
    setIsColorModalOpen(true);
  }

  function handleColorModalToggle() {
    setIsColorModalOpen(!isColorModalOpen);
  }

  function actionResolver(rowData, { rowIndex }) {
    if (rowData.cells.length === 1) return null;

    return [
      {
        title: "Set version category",
        onClick: (event, rowId, rowData, extra) => {
          onColorModelOpen();
          setVersionToSetColor(versionRows[rowId]);
        },
      },
      {
        isSeparator: true,
      },
      {
        title: "Remove this version",
        onClick: (event, rowId, rowData, extra) => {
          onDeleteModelOpen();
          setVersionToDelete(versionRows[rowId]);
        },
      },
    ];
  }

  useEffect(() => {
    if (versions) {
      setVersionRows(convertVersionsToVersionRows(versions));
    }
  }, []);

  useEffect(() => {
    if (versionRows) {
      setRows(convertToRows(versionRows));
    }
  }, [versionRows]);

  return (
    <>
      <Table
        aria-label={`product-table-${props.product.name}`}
        variant={TableVariant.compact}
        cells={headers}
        rows={rows}
        actionResolver={actionResolver}
      >
        <TableHeader />
        <TableBody />
      </Table>
      <DeleteConfirmationModal
        name={`${versionToDelete.name}`}
        type={"version"}
        versionId={versionToDelete.id}
        isModalOpen={isDeleteModalOpen}
        handleModalToggle={handleDeleteModalToggle}
      />
      <VersionColorModal
        name={`${versionToSetColor.name}`}
        version={versionToSetColor}
        isModalOpen={isColorModalOpen}
        handleModalToggle={handleColorModalToggle}
      />
    </>
  );
};
